import OrderQuote, { ErrorOrderQuote } from 'requests/objects/order-quote'
import ApiHandler from 'requests/apiHandler'
import OrderQuotePricing from 'requests/objects/order-quote-pricing'

/**
 * @template T
 * @typedef {{ fetch: () => Promise<T>; cancel: () => void; }} RequestApi
 */

/**
 * OrderQuotesHandler
 * @augments {ApiHandler<OrderQuote, ErrorOrderQuote>}
 */
export default class OrderQuotesHandler extends ApiHandler {
    constructor() {
        super({
            type: OrderQuote,
            errorType: ErrorOrderQuote,
            key: 'order-quotes',
        })
    }

    /**
     * validate order quote
     * @param {number} orderQuoteId orderQuoteId param
     * @param {object} data workflowStepId param
     * @param {boolean} data.isValidated isValidated data
     * @returns {RequestApi<OrderQuote>} Request
     */
    validateOrderQuote(orderQuoteId, data) {
        const request = this.initFetchRequest({
            url: [orderQuoteId, 'validate'],
            method: 'POST',
            data,
        })

        return this.getRequestApi(
            () => request.fetchRequest
                // eslint-disable-next-line new-cap
                .then(res => new OrderQuote(res.data['order-quotes']) ?? {})
                .catch(err => {
                    throw this.handleError(err)
                }),
            request.cancelToken,
        )
    }

    /**
     * Create order quote pricing
     * @param {number} orderQuoteId orderQuoteId param
     * @param {OrderQuotePricing} orderQuotePricing orderQuotePricing param
     * @returns {RequestApi<OrderQuotePricing>} Request
     */
    createOrderQuotePricing(orderQuoteId, orderQuotePricing) {
        const request = this.initFetchRequest({
            url: [orderQuoteId, 'pricing'],
            method: 'POST',
            data: orderQuotePricing,
        })

        return this.getRequestApi(
            () => request.fetchRequest
                // eslint-disable-next-line new-cap
                .then(res => new OrderQuotePricing(res.data['order-quotes']))
                .catch(err => {
                    throw this.handleError(err)
                }),
            request.cancelToken,
        )
    }

    /**
     * Create order quote pricing
     * @param {number} orderQuoteId orderQuoteId param
     * @param {OrderQuotePricing} orderQuotePricing orderQuotePricing param
     * @returns {RequestApi<OrderQuotePricing>} Request
     */
    updateOrderQuotePricing(orderQuoteId, orderQuotePricing) {
        const request = this.initFetchRequest({
            url: [orderQuoteId, 'pricing'],
            method: 'PUT',
            data: orderQuotePricing,
        })

        return this.getRequestApi(
            () => request.fetchRequest
                // eslint-disable-next-line new-cap
                .then(res => new OrderQuotePricing(res.data['order-quotes']))
                .catch(err => {
                    throw this.handleError(err)
                }),
            request.cancelToken,
        )
    }

    /**
     * deleteOrderQuotePricing
     * @param {number} orderQuoteId orderQuoteId param
     * @param {number} orderQuotePricingId orderQuotePricingId param
     * @returns {RequestApi<OrderQuotePricing>} Request
     */
    deleteOrderQuotePricing(orderQuoteId, orderQuotePricingId) {
        const request = this.initFetchRequest({
            url: [orderQuoteId, 'pricing', orderQuotePricingId],
            method: 'DELETE',
        })

        return this.getRequestApi(
            () => request.fetchRequest
                // eslint-disable-next-line new-cap
                .then(res => new OrderQuotePricing(res.data['order-quotes']))
                .catch(err => {
                    throw this.handleError(err)
                }),
            request.cancelToken,
        )
    }

    getAllFromPage(params = {}, currentPage = 1, recordsPerPage = 20) {
        const request = this.initFetchRequest({
            params: {
                ...params,
                currentPage,
                recordsPerPage,
            },
        })

        return this.getRequestApi(
            () => request.fetchRequest
                .then(res => res.data)
                .then(({ orderQuotes, totalRecords, totalPages }) => {
                    const items = (orderQuotes ?? []).map(o => new OrderQuote(o))
                    return { items, totalRecords, totalPages }
                })
                .catch(err => { throw this.handleError(err) }),
            request.cancelToken,
        )
    }

    /**
     * Download order-quote PDF.
     * @param {number} orderQuoteId Order quote id to download.
     * @param {string} lang Language of the PDF to download.
     * @returns {import('requests/apiHandler').RequestApi<{fileName: string, blob: Blob}>} Request.
     */
    printOrderQuote(orderQuoteId, lang) {
        const request = this.initFetchRequest({
            url: [orderQuoteId, 'print'],
            method: 'GET',
            params: { lang },
            responseType: 'arraybuffer',
            headers: { Accept: 'application/pdf' },
        })

        return this.getRequestApi(
            () => request.fetchRequest
                .then(res => {
                    const cd = res.headers['content-disposition'] || ''
                    const fileName = decodeURI(cd)?.split('filename=')?.[1]?.replace(/"/g, '')
                        || `devis-${orderQuoteId}.pdf`

                    return {
                        fileName,
                        blob: new Blob([res.data], { type: 'application/pdf' }),
                    }
                })
                .catch(err => { throw this.handleError(err) }),
            request.cancelToken,
        )
    }

    // eslint-disable-next-line class-methods-use-this
    sanitizeMerchandises(merchandises = []) {
        const toNumber = v => {
            if (v === null || v === undefined || v === '')
                return null

            const n = Number(String(v).replace(',', '.'))
            return Number.isFinite(n) ? n : null
        }

        const tofootage = v => {
            const n = toNumber(v)
            if (n === null || n <= 0)
                return null
            return n
        }

        return merchandises.map(m => ({
            ...m,
            articleId: m.isManualArticle ? null : (m.articleId ?? null),
            merchandiseName: m.isManualArticle ? (m.articleName ?? null) : null,
            long: toNumber(m.long),
            width: toNumber(m.width),
            height: toNumber(m.height),
            weight: toNumber(m.weight),
            quantity: toNumber(m.quantity) ?? 1,
            footage: tofootage(m.footage),
        }))
    }

    create(orderQuote, fileToken) {
        const request = this.initFetchRequest({
            method: 'POST',
            data: {
                ...orderQuote,
                fileToken,
                merchandises: this.sanitizeMerchandises(orderQuote.merchandises),
            },
        })

        return this.getRequestApi(
            () => request.fetchRequest
                .then(res => new OrderQuote(res.data['order-quotes']) ?? {})
                .catch(err => {
                    throw this.handleError(err)
                }),
            request.cancelToken,
        )
    }

    updateById(orderQuote, id) {
        const request = this.initFetchRequest({
            url: [id],
            method: 'PUT',
            data: {
                ...orderQuote,
                merchandises: this.sanitizeMerchandises(orderQuote.merchandises),
            },
        })

        return this.getRequestApi(
            () => request.fetchRequest
                .then(res => new OrderQuote(res.data['order-quotes']) ?? {})
                .catch(err => {
                    throw this.handleError(err)
                }),
            request.cancelToken,
        )
    }

    /**
     * Export all
     * @param {import('axios').AxiosRequestConfig['params']=} params Params
     * @returns {import('requests/apiHandler').RequestApi<{blob: Blob, fileName: string}>} Request
     */
    exportAll(params = {}) {
        const request = this.initFetchRequest({
            url: ['export'],
            method: 'GET',
            responseType: 'arraybuffer',
            params,
        })
        return this.getRequestApi(
            () => request.fetchRequest
                // eslint-disable-next-line new-cap
                .then(res => ({
                    fileName: decodeURI(res.headers['content-disposition'])?.split('attachment;filename=')?.[1] ?? 'EXPORT-Transports-RAUD.xlsx',
                    blob: new Blob([res.data]),
                }))
                .catch(err => {
                    throw this.handleError(err)
                }),
            request.cancelToken,
        )
    }
}
